// Client wrapper for the platform-staff admin endpoints (served same-origin at
// /api/admin/*). Mirrors lib/teamApi.ts: thin fetch helpers that forward the
// PocketBase session token so the sidecar can resolve + re-check `isStaff`. All
// of these are staff-only; the server returns 403 otherwise.

import type { RecordModel } from 'pocketbase'
import type { PlanId } from './types'
import { onAuthExpired } from './pb'

const BASE = '/api/admin'

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  opts: { body?: unknown; pbToken?: string; query?: Record<string, string> } = {},
): Promise<T> {
  const qs = opts.query ? `?${new URLSearchParams(opts.query)}` : ''
  const res = await fetch(`${BASE}${path}${qs}`, {
    method,
    headers: {
      ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
      ...(opts.pbToken ? { 'X-PB-Token': opts.pbToken } : {}),
    },
    body: method === 'POST' ? JSON.stringify(opts.body ?? {}) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (res.status === 401) onAuthExpired()
  if (!res.ok) throw new Error((data as { error?: string })?.error || 'Admin request failed.')
  return data as T
}

// ── reporting ────────────────────────────────────────────────────────────────

export interface MetricsSummary {
  totalAccounts: number
  paidAccounts: number
  activeSubscriptions: number
  mrrCents: number
  arrCents: number
  recruitersCount: number
  candidatesCount: number
  waitlistTotal: number
  activityTotal: number
}
export interface PlanDist {
  planId: PlanId
  count: number
  mrrCents: number
}
export interface MrrPoint {
  month: string
  mrrCents: number
  payingCount: number
}
export interface ChurnPoint {
  month: string
  canceled: number
  rate: number
}
export interface SignupPoint {
  month: string
  count: number
}
export interface ActivityPoint {
  date: string
  count: number
}
export interface RecentEvent {
  accountId: string
  accountName: string
  kind: string
  planId: PlanId
  prevPlanId: PlanId | ''
  mrrCents: number
  at: string
}
export interface AdminMetrics {
  generatedAt: string
  summary: MetricsSummary
  planDistribution: PlanDist[]
  mrrSeries: MrrPoint[]
  churnSeries: ChurnPoint[]
  signupsSeries: SignupPoint[]
  activitySeries: ActivityPoint[]
  recentEvents: RecentEvent[]
}

export function getMetrics(pbToken: string): Promise<AdminMetrics> {
  return request('GET', '/metrics', { pbToken })
}

export interface Subscriber {
  accountId: string
  name: string
  domain: string
  ownerEmail: string
  planId: PlanId
  mrrCents: number
  seats: number
  status: 'active' | 'past_due' | 'canceled'
  createdSource: string
  createdAt: string
  renewsOn: string
  stripeSubscriptionId: string
  cancelAtPeriodEnd: boolean
}

export function getSubscribers(pbToken: string): Promise<{ subscribers: Subscriber[] }> {
  return request('GET', '/subscribers', { pbToken })
}

// ── announcements ──────────────────────────────────────────────────────────────

export type AnnouncementChannel = 'banner' | 'bell' | 'email' | 'push' | 'marketing'
export type AnnouncementSeverity = 'info' | 'warn' | 'critical'
export type AnnouncementStatus = 'draft' | 'published' | 'archived'

export interface AnnouncementDraft {
  id?: string
  title: string
  body: string
  channels: AnnouncementChannel[]
  audience: 'all' | 'plan' | 'account'
  audiencePlan?: string
  audienceAccountId?: string
  severity: AnnouncementSeverity
  expiresAt?: string
  dismissible?: boolean
}

export function listAnnouncements(pbToken: string): Promise<{ announcements: RecordModel[] }> {
  return request('GET', '/announcements', { pbToken })
}
export function createAnnouncement(draft: AnnouncementDraft, pbToken: string): Promise<{ announcement: RecordModel }> {
  return request('POST', '/announcements', { body: draft, pbToken })
}
export function saveAnnouncement(draft: AnnouncementDraft, pbToken: string): Promise<{ announcement: RecordModel }> {
  return request('POST', '/announcements/save', { body: draft, pbToken })
}
export function publishAnnouncement(
  id: string,
  pbToken: string,
): Promise<{ announcement: RecordModel; delivery: { email: number } }> {
  return request('POST', '/announcements/publish', { body: { id }, pbToken })
}
export function deleteAnnouncement(id: string, pbToken: string): Promise<{ ok: true }> {
  return request('POST', '/announcements/delete', { body: { id }, pbToken })
}
